/**
 * # Experimentation
 *
 * Experiments are defined in code at build time.
 * Treatments are assigned at runtime as needed.
 * Treatments are persisted in cookies.
 * Treatments are first assigned during server rendering, but may be overridden on the client.
 * Treatments are assigned randomly based on bucket weights.
 *
 * Experiment names must be globally unique.
 * Bucket names must be unique within an experiment.
 * Bucket weights must sum to 100.
 * Bucket values are optional
 *
 */

import type { CookiesCollection, CookieProps } from './cookie-helpers';

export type Bucket = {
  name: string;
  description?: string;
  weight: number;
  value?: string;
};

export type Experiment = {
  name: string;
  started: Date;
  version: number;
  buckets: Bucket[];
};

export type Treatment = {
  experiment: Experiment;
  bucket: Bucket;
};

export type TreatmentsMap = Record<string, Treatment>;

const EXPERIMENT_PREFIX = 'ab_';

export const MODERN_AUTH_EXPERIMENT = 'modern_auth_001';

const ALL_EXPERIMENTS: Experiment[] = [
  {
    name: 'landing_hero_img_002',
    version: 1,
    started: new Date('2023-12-06'),
    buckets: [
      {
        name: 'control',
        weight: 50,
        value: 'hero-image-02.jpg',
      },
      {
        name: 'treatment',
        weight: 50,
        value: 'hero-image-03.jpg',
      },
    ],
  },
  {
    // Operational rollout flag for the modern OAuth2 authentication system.
    // The (currently zero-weight) treatment can be selected explicitly in dev
    // via experiment cookie, but is not yet assigned to production traffic.
    name: MODERN_AUTH_EXPERIMENT,
    version: 1,
    started: new Date('2026-07-12'),
    buckets: [
      {
        name: 'control',
        description: 'Legacy authentication across all app entry points',
        weight: 100,
      },
      {
        name: 'modern_auth',
        description: 'Modern OAuth2 authentication across all app entry points',
        weight: 0,
      },
    ],
  },
];

/**
 * # getAllTreatments
 * Finds or assigns Treatments for all experiments to the current user.
 *
 * Treatments may already be stored in cookies, in which case they are returned, otherwise
 * a new treatment is assigned and returned (not written to cookies due to NextJS limitations).
 *
 * Can be run on server or client as it does not write to cookies.
 *
 * @param cookies A collection of cookies in name/value pairs
 * @returns A mapping of experiment names to Treatments
 */
export function getAllTreatments(cookies: CookiesCollection): TreatmentsMap {
  // console.log('getAllTreatments');
  const treatments: TreatmentsMap = {};
  ALL_EXPERIMENTS.forEach((experiment) => {
    const treatment = getTreatment(experiment, cookies);
    if (treatment !== null) {
      treatments[experiment.name] = treatment;
    }
  });
  return treatments;
}

/**
 * # getTreatment
 * Finds an existing treatment for the given experiment, or assigns a new one to the current user.
 *
 * Treatments may already be stored in cookies, in which case they are returned, otherwise
 * a new treatment is assigned and returned (not written to cookies due to NextJS limitations).
 *
 * Can be run on server or client as it does not write to cookies.
 *
 * @param experimentOrName The experiment to get a treatment for
 * @param cookies A collection of cookies as name/value pairs
 * @returns A Treatment or null if the experiment is invalid
 */
export function getTreatment(
  experimentOrName: string | Experiment,
  cookies: CookiesCollection,
): Treatment | null {
  // console.log('getTreatment', experimentOrName);
  const experimentName = getExperimentName(experimentOrName);

  const experiment = getExperiment(experimentName);
  if (experiment === null) {
    console.warn('Experiment not found', experimentName);
    return null;
  }

  const treatment = readTreatment(experiment, cookies);
  if (treatment !== null) {
    return treatment;
  }

  return assignTreatment(experiment);
}

/**
 * # getExperiment
 * Fetches the experiment definition by name or by object for ambiguous function params.
 *
 * @param experimentOrName The experiment to fetch
 * @returns The Experiment found or null if not found
 */
export function getExperiment(experimentOrName: string | Experiment): Experiment | null {
  const experimentName =
    typeof experimentOrName === 'string' ? experimentOrName : experimentOrName.name;
  return ALL_EXPERIMENTS.find((e) => e.name === experimentName) || null;
}

/**
 * # getExperimentName
 * Extracts the experiment name string from ambiguous function params.
 *
 * @param experimentOrName The experiment to get the name of which could be a string or an Experiment
 * @returns The string Experiment name if a string was passed in, or the name of the Experiment object
 */
export function getExperimentName(experimentOrName: string | Experiment): string {
  return typeof experimentOrName === 'string' ? experimentOrName : experimentOrName.name;
}

/**
 * # assignTreatment
 * Randomly assigns a treatment to the user, taking into account bucket weights.
 * Does not write to cookies.
 *
 * @param experiment The experiment to assign a treatment for
 * @returns The Treatment assigned
 */
function assignTreatment(experiment: Experiment): Treatment {
  const cumulativeWeights = experiment.buckets.reduce((acc, bucket) => {
    const lastWeight = acc[acc.length - 1] || 0;
    return [...acc, lastWeight + bucket.weight];
  }, [] as number[]);
  const maxCumulativeWeight = cumulativeWeights[cumulativeWeights.length - 1];
  const randomNumber = maxCumulativeWeight * Math.random();
  // console.log('Assigning treatment', experiment.name, { cumulativeWeights, maxCumulativeWeight });
  // console.log('Assigning treatment', experiment.name, randomNumber);

  let bucket = experiment.buckets.find((_b, idx) => cumulativeWeights[idx] >= randomNumber);
  if (typeof bucket === 'undefined') {
    // console.log('no bucket found at random number, using control');
    bucket = experiment.buckets.find((b) => b.name === 'control') as Bucket;
  }
  // console.log('found bucket', bucket.name);
  // console.log(
  //   'Assigning treatment',
  //   experiment.name,
  //   bucket.name,
  //   cumulativeWeights,
  //   maxCumulativeWeight,
  //   randomNumber
  // );

  const treatment = {
    experiment,
    bucket,
  };

  return treatment;
}

/**
 * # readTreatment
 * Reads the treatment from cookies and only returns it if it exists.
 * Can be called client or server as it does not write to cookies.
 *
 * Typically used internally in this module, but may be used elsewhere for side-effect-free
 * fetching of treatments.
 *
 * @param experimentOrName The name of the experiment to read or the experiment object
 * @param cookies The cookies store which may be readonly
 * @returns The Treatment found or null if not found
 */
export function readTreatment(
  experimentOrName: string | Experiment,
  cookies: CookiesCollection,
): Treatment | null {
  const experimentName = getExperimentName(experimentOrName);
  const experiment = getExperiment(experimentName);
  if (!experiment) {
    console.warn('Experiment not found', experimentName);
    return null;
  }

  const bucketName = cookies[getCookieName(experimentName)];
  if (typeof bucketName === 'undefined' || bucketName === null) {
    // console.log('readTreatment, no cookie found at:', `${EXPERIMENT_PREFIX}${experimentName}`);
    return null;
  }
  // console.log('readTreatment cookie found bucket:', bucketName);
  const bucket = experiment.buckets.find((b) => b.name === bucketName);
  if (!bucket) {
    console.warn('Experiment bucket not found', experimentName, bucketName);
    return null;
  }

  const treatment = {
    experiment,
    bucket,
  };

  return treatment;
}

/**
 * # buildTreatmentCookie
 * Generates a cookie POJO in a standardized format for the given Treatment and returns it.
 *   ab_<experiment name>: <bucket name>
 *
 * **Does not store anything, just returns a POJO.**
 *
 * @param treatment The Treatment to generate a cookie for
 * @returns CookieProps POJO
 */
export function buildTreatmentCookie(treatment: Treatment): CookieProps {
  const { experiment, bucket } = treatment;
  const expires = new Date();
  // max cookie expiration date is 400 days in the future
  expires.setTime(expires.getTime() + 1000 * 60 * 60 * 24 * 400);
  return {
    name: getCookieName(experiment),
    value: bucket.name,
    secure: true,
    expires,
  };
}

/**
 * # getCookieName
 * Generates a standardized cookie name for the given experiment. Practically this means prefixing
 * the experiment name with our custom prefix and ensuring it uses valid characters.
 *
 * @param experimentOrName The experiment or its name to generate a cookie name from
 * @returns The cookie name as a string
 */
export function getCookieName(experimentOrName: string | Experiment): string {
  const experimentName = getExperimentName(experimentOrName).trim().replace(/\W/g, '_');
  return `${EXPERIMENT_PREFIX}${experimentName}`;
}

/**
 * # getSuperProp
 * Generates a super property key value pair for the given Treatment in a standardized format.
 *  ab_<experiment name>: <bucket name> (v<experiment version>)
 *
 * @param treatment The experiment Treatment to get the super property for
 * @returns A super property key value pair for the given Treatment
 */
export function getSuperProp(treatment: Treatment): Record<string, string> {
  const { experiment, bucket } = treatment;
  return {
    [getCookieName(experiment)]: `${bucket.name} (v${experiment.version})`,
  };
}
